import { CSSProperties, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Alert, Anchor, Button, Flex, Icon, Modal, Text, Typography, useToast } from '@percent/lemonade'
import { useCausesPortalAnalytics, useIntroModalState } from '@percent/cause-dashboard/common/hooks'
import {
  GFM_INTEREST_EVENTS,
  type GfmDismissMethod
} from '@percent/cause-dashboard/common/components/gfmInterest/gfmInterestEvents'
import type { GofundmeUserJourneysVariant } from '@percent/cause-dashboard/constants/gofundmeUserJourneys'
import { RoutePath } from '@percent/cause-dashboard/app/routes/Routes'
import type { LocaleKey } from '@percent/cause-dashboard/i18n/config'
import styles from './InterestModal.module.scss'

// NGM-160. Pierre's artboards 9 and 10 pin these to low intent with a decline CTA, so only the
// theme varies; the smoke test's intent and dismiss-style axes are gone.
const variantCopy: Record<
  GofundmeUserJourneysVariant,
  { title: LocaleKey; description: LocaleKey; cta: LocaleKey; rightColumnClass: string; infoLine?: LocaleKey }
> = {
  tools: {
    title: 'typography.gfmInterestModal.tools.title',
    description: 'typography.gfmInterestModal.tools.description',
    cta: 'typography.gfmInterestModal.tools.cta',
    rightColumnClass: styles.rightColumnTools
  },
  supporters: {
    title: 'typography.gfmInterestModal.supporters.title',
    description: 'typography.gfmInterestModal.supporters.description',
    cta: 'typography.gfmInterestModal.supporters.cta',
    rightColumnClass: styles.rightColumnSupporters
  },
  supporters_b: {
    title: 'typography.gfmInterestModal.supporters_b.title',
    description: 'typography.gfmInterestModal.supporters_b.description',
    cta: 'typography.gfmInterestModal.supporters_b.cta',
    rightColumnClass: styles.rightColumnSupporters
  },
  tools_b: {
    title: 'typography.gfmInterestModal.tools_b.title',
    description: 'typography.gfmInterestModal.tools_b.description',
    cta: 'typography.gfmInterestModal.tools_b.cta',
    rightColumnClass: styles.rightColumnTools
  },
  goodstack_supporters: {
    title: 'typography.gfmInterestModal.goodstack_supporters.title',
    description: 'typography.gfmInterestModal.goodstack_supporters.description',
    cta: 'typography.gfmInterestModal.goodstack_supporters.cta',
    rightColumnClass: styles.rightColumnGoodstackSupporters
  },
  goodstack_tools: {
    title: 'typography.gfmInterestModal.goodstack_tools.title',
    description: 'typography.gfmInterestModal.goodstack_tools.description',
    cta: 'typography.gfmInterestModal.goodstack_tools.cta',
    rightColumnClass: styles.rightColumnGoodstackTools,
    infoLine: 'typography.gfmInterestModal.goodstack_tools.infoLine'
  }
}

type InterestModalProps = {
  variant: GofundmeUserJourneysVariant
  width: number
  height: number
  onDismiss: () => void
}

export function InterestModal({ variant, width, height, onDismiss }: Readonly<InterestModalProps>) {
  const { t } = useTranslation()
  const { track } = useCausesPortalAnalytics()
  const { addToast } = useToast()
  const { recordGfmInterestModalView, recordGfmInterestRegistered, recordGfmInterestDeclined } = useIntroModalState()

  const [hasRegistered, setHasRegistered] = useState(false)
  const hasRecordedView = useRef(false)

  const copy = variantCopy[variant]
  const isGoodstack = variant === 'goodstack_supporters' || variant === 'goodstack_tools'
  const eventProperties = { variant, element: 'modal' as const }

  useEffect(() => {
    if (hasRecordedView.current) {
      return
    }
    hasRecordedView.current = true
    track(GFM_INTEREST_EVENTS.shown, eventProperties)
    recordGfmInterestModalView()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track, recordGfmInterestModalView, variant])

  const handleCtaClick = () => {
    track(GFM_INTEREST_EVENTS.ctaClicked, eventProperties)
    recordGfmInterestRegistered()
    setHasRegistered(true)
    addToast(t('typography.gfmInterestModal.confirmation.alert'), 'success')
  }

  const handleDismiss = (dismissMethod: GfmDismissMethod) => () => {
    // Escape is not an answer: they may not have read it, so the modal can still return
    if (dismissMethod === 'not_interested') {
      recordGfmInterestDeclined()
    }

    track(GFM_INTEREST_EVENTS.dismissed, { ...eventProperties, dismissMethod })
    onDismiss()
  }

  const title = (
    <Typography variant="h2" className={styles.title}>
      {t(copy.title)}
    </Typography>
  )

  return (
    <div id="interest-modal-container">
      <Modal
        open
        onClose={hasRegistered ? onDismiss : handleDismiss('esc')}
        width={width}
        height={height}
        overflowHidden
        data-testid="interest-modal"
      >
        <div
          className={styles.modalCtn}
          style={
            { '--interest-modal-height': `${height}px`, '--interest-modal-max-width': `${width}px` } as CSSProperties
          }
        >
          <div className={`${styles.column} ${styles.leftColumn}`}>
            <Flex direction="column" justify="space-between" className={styles.leftCtn}>
              {hasRegistered ? (
                <>
                  <Flex direction="column" gap="var(--spacing-xl)">
                    {title}
                    <Alert variant="success">{t('typography.gfmInterestModal.confirmation.alert')}</Alert>
                    <Text>
                      <Trans
                        i18nKey={
                          isGoodstack
                            ? 'typography.gfmInterestModal.confirmation.descriptionGoodstack'
                            : 'typography.gfmInterestModal.confirmation.description'
                        }
                        components={{
                          a: (
                            <Anchor
                              component={Link}
                              to={RoutePath.MY_ACCOUNT}
                              c="var(--semantic-interactive-default)"
                              underline="always"
                            />
                          )
                        }}
                      />
                    </Text>
                  </Flex>
                  <div className={isGoodstack ? styles.ctaButtonDefault : styles.ctaButton}>
                    <Button stretch onClick={onDismiss} data-testid="interest-modal-next">
                      {t('button.done')}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Flex direction="column" gap="var(--spacing-xl)">
                    {title}
                    <Text size="large" color="var(--semantic-content-secondary)">
                      <Trans
                        // @ts-expect-error - i18nKey is not typed correctly
                        i18nKey={copy.description}
                        components={{ highlight: <span className={styles.highlight} /> }}
                      />
                    </Text>
                  </Flex>
                  <Flex direction="column" gap="var(--spacing-xl)">
                    <Flex direction="column" gap="var(--spacing-xs)">
                      {copy.infoLine && (
                        <div className={styles.infoLine} data-testid="interest-modal-info">
                          <Icon name="info" size={4} />
                          <Text size="small" color="var(--semantic-interactive-default)">
                            {t(copy.infoLine)}
                          </Text>
                        </div>
                      )}
                      <div className={isGoodstack ? styles.ctaButtonDefault : styles.ctaButton}>
                        <Button stretch onClick={handleCtaClick} data-testid="interest-modal-cta">
                          {t(copy.cta)}
                        </Button>
                      </div>
                      <div className={styles.dismissButton}>
                        <Button
                          stretch
                          variant="tertiary"
                          onClick={handleDismiss('not_interested')}
                          data-testid="interest-modal-dismiss"
                        >
                          {t('typography.gfmInterestModal.dismiss')}
                        </Button>
                      </div>
                    </Flex>
                  </Flex>
                </>
              )}
            </Flex>
          </div>
          <div className={`${styles.column} ${styles.rightColumn} ${copy.rightColumnClass}`} />
        </div>
      </Modal>
    </div>
  )
}
